// src/features/equipment/pages/EquipmentListPage.jsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Alert,
  Tooltip,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Switch,
  FormControlLabel,
  TextField,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Devices as DevicesIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  MeetingRoom as MeetingRoomIcon,
  ArrowDropDown as ArrowDropDownIcon,
  NoteAdd as NoteAddIcon,
  FileCopy,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import LocalizedDateDistance from '../../../i18n/LocalizedDateDistance';

// Hooks
import { useEquipment } from '../hooks/useEquipment';
import { useRooms } from '../../rooms/hooks/useRooms';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { useConfirmDialog } from '../../../hooks/mui/useConfirmDialog';
import { usePermissions } from '../../auth/hooks/usePermissions';

// Utils
import { getEquipmentImageByModel } from '../utils/getEquipmentImage';

import PageHeader from '../../../components/common/PageHeader';
import EquipmentStatusChip, { EQUIPMENT_STATUSES } from '../components/EquipmentStatusChip';
import BiotechIcon from '@mui/icons-material/Biotech';

const CLASSIFICATION_COLORS = {
  UNCLASSIFIED: 'default',
  SUPERVISED_AREA: 'warning',
  CONTROLLED_AREA: 'error',
};

export default function EquipmentListPage() {
  const { t } = useTranslation(['equipment', 'rooms', 'common']);
  const navigate = useNavigate();
  const { showSuccess, showError } = useSnackbar();
  const { showConfirmDialog, ConfirmDialog } = useConfirmDialog();
  const permissions = usePermissions();

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [roomId, setRoomId] = useState('');
  const [listType, setListType] = useState('inventory');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

  // Action menu state
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);
  const [selectedEquipment, setSelectedEquipment] = useState(null);

  // API hooks
  const {
    equipment,
    loading,
    error,
    clearError,
    filterEquipment,
    deleteEquipment,
  } = useEquipment();

  const { rooms, fetchDepartmentRooms } = useRooms();
  const canReadRooms = permissions.hasScope('room:read');

  // Load rooms once (only for the room filter, which needs room:read)
  useEffect(() => {
    if (canReadRooms) fetchDepartmentRooms();
  }, [fetchDepartmentRooms, canReadRooms]);

  // Debounced filter fetch
  const debounceRef = useRef(null);

  const runFilter = useCallback(() => {
    const params = { list_type: listType, include_inactive: includeInactive };
    if (searchTerm.trim()) params.search = searchTerm.trim();
    if (roomId) params.room_id = roomId;
    if (statusFilter) params.status = statusFilter;
    filterEquipment(params);
  }, [filterEquipment, searchTerm, roomId, listType, includeInactive, statusFilter]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(runFilter, searchTerm ? 400 : 0);
    return () => clearTimeout(debounceRef.current);
  }, [searchTerm, roomId, listType, includeInactive, statusFilter, runFilter]);

  const handleRefresh = useCallback(() => runFilter(), [runFilter]);

  // Event handlers
  const handleViewEquipment = useCallback((equipment) => {
    navigate(`/equipment/${equipment.equipment_id}`);
  }, [navigate]);

  const handleEditEquipment = useCallback((equipment) => {
    navigate(`/equipment/${equipment.equipment_id}/edit`);
  }, [navigate]);

  const handleMenuOpen = useCallback((event, equipment) => {
    event.stopPropagation();
    setSelectedEquipment(equipment);
    setActionMenuAnchor(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setActionMenuAnchor(null);
    setSelectedEquipment(null);
  }, []);

  const handleAddMenuOpen = useCallback((event) => {
    setAddMenuAnchor(event.currentTarget);
  }, []);

  const handleAddMenuClose = useCallback(() => {
    setAddMenuAnchor(null);
  }, []);

  const handleAddFromTemplate = useCallback(() => {
    handleAddMenuClose();
    navigate('select-template');
  }, [handleAddMenuClose, navigate]);

  const handleAddFromScratch = useCallback(() => {
    handleAddMenuClose();
    navigate('create');
  }, [handleAddMenuClose, navigate]);

  const handleDeleteEquipment = useCallback((equipment) => {
    showConfirmDialog({
      title: t('equipment:confirm.delete_equipment_title'),
      message: t('equipment:confirm.delete_equipment_message', { name: equipment.name }),
      confirmText: t('equipment:confirm.delete_confirm'),
      cancelText: t('common:buttons.cancel'),
      severity: 'error',
      onConfirm: async () => {
        try {
          const result = await deleteEquipment(equipment.equipment_id);
          if (result.success) {
            showSuccess(t('equipment:success.equipment_deleted'));
            runFilter();
          } else {
            showError(result.error || t('equipment:errors.delete_failed'));
          }
        } catch {
          showError(t('equipment:errors.delete_failed'));
        }
      },
    });
    handleMenuClose();
  }, [showConfirmDialog, deleteEquipment, showSuccess, showError, handleMenuClose, runFilter, t]);

  // Permissions
  const canCreate = permissions.hasScope('equipment:create');
  const canUpdate = permissions.hasScope('equipment:update');
  const canDelete = permissions.hasScope('equipment:delete');

  // DataGrid columns
  const columns = useMemo(() => [
    {
      field: 'name',
      headerName: t('equipment:grid.columns.equipment_name'),
      flex: 1,
      minWidth: 250,
      renderCell: (params) => (
        <Box display="flex" alignItems="strech" gap={2} height="100%" sx={{ py: 1 }}>
          {getEquipmentImageByModel(params.row.model) ? (
            <Box
              sx={{
                width: 100,
                minWidth: 100,
                backgroundImage: `url(${getEquipmentImageByModel(params.row.model)})`,
                backgroundSize: 'contain',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                borderRadius: 1,
              }}
            />
          ) : (
            <Box sx={{ width: 100, minWidth: 100 }} display="flex" alignItems="center" />
          )}
          <Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" fontWeight="medium">
                {params.value}
              </Typography>
            </Box>
          </Box>
        </Box>
      ),
    },
    {
      field: 'equipment_type',
      headerName: t('equipment:grid.columns.type'),
      width: 180,
      renderCell: (params) => (
        <Chip
          label={t(`equipment:equipment_types.${params.value}`)}
          size="small"
          color="primary"
          variant="outlined"
        />
      ),
    },
    {
      field: 'manufacturer',
      headerName: t('equipment:grid.columns.manufacturer'),
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2">{params.value || '-'}</Typography>
      ),
    },
    {
      field: 'model',
      headerName: t('equipment:grid.columns.model'),
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2">{params.value || '-'}</Typography>
      ),
    },
    {
      field: 'room',
      headerName: t('equipment:grid.columns.room'),
      width: 200,
      renderCell: (params) => {
        const room = params.row.room;
        if (!room) {
          return <Typography variant="body2" color="text.secondary">-</Typography>;
        }
        return (
          <Box>
            <Box display="flex" alignItems="center" gap={1}>
              <MeetingRoomIcon fontSize="small" color="action" />
              <Typography variant="body2">{room.name}</Typography>
            </Box>
            <Chip
              label={t(`rooms:classifications.${room.classification}`)}
              size="small"
              color={CLASSIFICATION_COLORS[room.classification]}
              variant="outlined"
              sx={{ mt: 0.5 }}
            />
          </Box>
        );
      },
      sortComparator: (v1, v2) => (v2?.name || '').localeCompare(v1?.name || ''),
    },
    {
      field: 'status',
      headerName: t('equipment:grid.columns.status'),
      width: 170,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => <EquipmentStatusChip status={params.value} />,
    },
    {
      field: 'created_at',
      headerName: t('equipment:grid.columns.added'),
      width: 140,
      renderCell: (params) => (
        <Typography variant="caption" color="text.secondary">
          {params.value ? <LocalizedDateDistance date={params.value} /> : t('equipment:grid.columns.unknown')}
        </Typography>
      ),
    },
    {
      field: 'actions',
      // Icon-only kebab column: no header, like the other list grids
      headerName: '',
      width: 80,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton size="small" onClick={(e) => handleMenuOpen(e, params.row)}>
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ], [handleMenuOpen, t]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/* Header */}
      <PageHeader
        icon={BiotechIcon}
        title={t('equipment:title')}
        subtitle={t('equipment:subtitle')}
        actions={<>
          <Tooltip title={t('equipment:actions.refresh')}>
            <IconButton onClick={handleRefresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>

          {canCreate && (
            <>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                endIcon={<ArrowDropDownIcon />}
                onClick={handleAddMenuOpen}
                disabled={loading}
              >
                {t('equipment:actions.add_equipment')}
              </Button>

              <Menu
                anchorEl={addMenuAnchor}
                open={Boolean(addMenuAnchor)}
                onClose={handleAddMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem onClick={handleAddFromTemplate}>
                  <ListItemIcon><FileCopy fontSize="small" /></ListItemIcon>
                  <ListItemText>{t('equipment:actions.add_from_template')}</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleAddFromScratch}>
                  <ListItemIcon><NoteAddIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>{t('equipment:actions.add_from_scratch')}</ListItemText>
                </MenuItem>
              </Menu>
            </>
          )}
        </>}
      />

      {/* Filter Row */}
      <Box display="flex" alignItems="center" gap={2} mb={3} flexWrap="wrap">
        <TextField
          size="small"
          placeholder={t('equipment:filter.search_placeholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          slotProps={{
            htmlInput: { autoFocus: true },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
          sx={{ flex: 1, minWidth: 220 }}
        />

        {canReadRooms && <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="equipment-list-page-room-label">{t('equipment:filter.room')}</InputLabel>
          <Select labelId="equipment-list-page-room-label"
            value={roomId}
            onChange={(e) => setRoomId(e.target.value)}
            label={t('equipment:filter.room')}
          >
            <MenuItem value="">{t('equipment:filter.all_rooms')}</MenuItem>
            {rooms.map((room) => (
              <MenuItem key={room.room_id} value={room.room_id}>
                {room.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>}

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="equipment-list-page-list-type-label">{t('equipment:filter.list_type')}</InputLabel>
          <Select labelId="equipment-list-page-list-type-label"
            value={listType}
            onChange={(e) => setListType(e.target.value)}
            label={t('equipment:filter.list_type')}
          >
            <MenuItem value="inventory">{t('equipment:filter.list_type_inventory')}</MenuItem>
            <MenuItem value="radiology">{t('equipment:filter.list_type_radiology')}</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="equipment-list-page-status-label">{t('equipment:filter.status')}</InputLabel>
          <Select labelId="equipment-list-page-status-label"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            label={t('equipment:filter.status')}
          >
            <MenuItem value="">{t('equipment:filter.all_statuses')}</MenuItem>
            {EQUIPMENT_STATUSES.map((status) => (
              <MenuItem key={status} value={status}>{t(`equipment:status.${status}`)}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControlLabel
          control={
            <Switch
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
              size="small"
            />
          }
          label={t('equipment:actions.include_inactive')}
        />
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      {/* DataGrid */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          width: '100%',
          '& .MuiDataGrid-root': {
            border: 'none',
            '& .MuiDataGrid-main': {
              '& .MuiDataGrid-columnHeaders': {
                borderBottom: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper',
              },
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                '&:focus': { outline: 'none' },
              },
              '& .MuiDataGrid-row': {
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'action.hover' },
              },
            },
          },
        }}
      >
        <DataGrid
          rows={equipment}
          columns={columns}
          getRowId={(row) => row.equipment_id}
          loading={loading}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          disableColumnMenu
          onRowClick={(params) => handleViewEquipment(params.row)}
          rowHeight={72}
          getRowHeight={() => 'auto'}
          initialState={{
            sorting: { sortModel: [{ field: 'room', sort: 'desc' }] },
          }}
          slots={{
            noRowsOverlay: () => (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                height="100%"
                py={4}
              >
                <DevicesIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {t('equipment:messages.no_equipment')}
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center" paragraph>
                  {t('equipment:messages.no_equipment_description')}
                </Typography>
              </Box>
            ),
          }}
          sx={{
            backgroundColor: 'background.paper',
            borderRadius: 2,
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid',
              borderColor: 'divider',
            },
          }}
        />
      </Box>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleMenuClose}
        PaperProps={{ elevation: 3 }}
      >
        <MenuItem onClick={() => { handleViewEquipment(selectedEquipment); handleMenuClose(); }}>
          <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t('equipment:actions.view_details')}</ListItemText>
        </MenuItem>

        {canUpdate && (
          <MenuItem onClick={() => { handleEditEquipment(selectedEquipment); handleMenuClose(); }}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t('equipment:actions.edit')}</ListItemText>
          </MenuItem>
        )}

        {canDelete && (
          <MenuItem onClick={() => { handleDeleteEquipment(selectedEquipment); handleMenuClose(); }}>
            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>
              <Typography color="error">{t('equipment:actions.delete')}</Typography>
            </ListItemText>
          </MenuItem>
        )}
      </Menu>

      <ConfirmDialog />
    </Box>
  );
}
